import React, { useRef, useMemo, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogContent,
} from "@mui/material";
import ExamHeader from "./ExamHeader";
import { useQuestionSection } from "../../../hooks/default/useQuestionSection";
import { useLanguages } from "../../../context/LanguagesContext";
import ButtonSection from "./ButtonSection";
import SubmissionPopupReLogin from "../../../components/popups/SubmissionPopupReLogin";
import SubmissionPopup from "../../../components/popups/SubmissionPopup";
import ServerErrorDialog from "../../../components/popups/ServerErrorDialog";
import FinalSubmissionDialog from "../../../components/popups/FinalSubmissionDialog";
import useQuestionSectionStyles from "../testStyle/gateStyle/questionSectionStyle";
import { QuestionRenderer } from "../default/QuestionRenderer";
import Loader from "../../../components/Loader";
import down from "../../../images/down.png";
import up from "../../../images/up.png";
import CloseIcon from "@mui/icons-material/Close";

function QuestionSection({
  subjectNames = [],
  sectionNames = [],
  questionData = [],
  currentQuestion = 0,
  setCurrentQuestion,
  subjectId,
  currentSubject = "",
  currentSection = "",
  setActiveSubjectId,
  setActiveSectionId,
  activeSubjectId,
  activeSectionId,
  desktopOpen,
  setSscTimeUp,
  setIsOnSubmit,
  isMobile,
}) {
  const { selectedLanguage } = useLanguages();
  const classes = useQuestionSectionStyles();
  const paperRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [selectedImg, setSelectedImg] = useState("");

  const filteredQuestions = useMemo(() => {
    return (
      questionData?.filter((q) => {
        try {
          return (
            q &&
            Number(q?.section_id) === Number(activeSectionId) &&
            Number(q?.subject_id) === Number(activeSubjectId)
          );
        } catch (error) {
          return false;
        }
      }) || []
    );
  }, [questionData, activeSectionId, activeSubjectId]);

  const question = useMemo(() => {
    if (!filteredQuestions || filteredQuestions.length === 0) {
      return null;
    }

    const q = filteredQuestions[currentQuestion];
    return q || null;
  }, [filteredQuestions, currentQuestion]);

  const {
    selectedOptionId,
    selectedOptionIds,
    inputAnswer,
    showSubmissionPopup,
    showSubmissionPopupReLogin,
    showServerErrorDialog,
    setShowServerErrorDialog,
    showSubmitModal,
    isTimeUp,
    countdownTime,
    finalSubmitChecked,
    submitError,
    countdownByHead,
    stats,
    handleOptionChange,
    handleMultipleChoiceChange,
    handleInputChange,
    resetSelections,
    handleSaveNextDefault,
    handleMarkReviewDefault,
    handleClearResponse,
    handleBack,
    handleNext,
    handleOpenSubmitModal,
    handleCloseSubmitModal,
    handleFinalSubmitCheck,
    handleFinalSubmit,
    handleSubmit,
  } = useQuestionSection({
    questionData: filteredQuestions,
    currentQuestion,
    setCurrentQuestion,
    subjectId,
    activeSectionId,
    activeSubjectId,
  });

  const handleQuestionChange = (index) => {
    if (index >= 0 && index < filteredQuestions.length) {
      setCurrentQuestion(index);
    }
  };

  const scrollToBottom = () => {
    if (paperRef.current) {
      paperRef.current.scrollTo({
        top: paperRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  const scrollToTop = () => {
    if (paperRef.current) {
      paperRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    scrollToTop();
  }, [currentQuestion]);

  const handleImageClick = (e) => {
    const src = e.target.src;
    if (src) {
      setSelectedImg(src);
      setOpen(true);
    }
  };

  return (
    <Box className={classes.rootContainer}>
      {/* Fixed Header */}
      <Box className={classes.headerContainer}>
        <ExamHeader
          subjectNames={subjectNames}
          currentQuestion={currentQuestion}
          currentSubject={currentSubject}
          sectionNames={sectionNames}
          handleQuestionChange={handleQuestionChange}
          currentSection={currentSection}
          questionData={filteredQuestions}
          allQuestion={questionData}
          setCurrentQuestion={setCurrentQuestion}
          subjectId={subjectId}
          negativeMark={question?.negative_mark}
          mark={question?.mark}
          setActiveSubjectId={setActiveSubjectId}
          setActiveSectionId={setActiveSectionId}
          activeSubjectId={activeSubjectId}
          activeSectionId={activeSectionId}
          desktopOpen={desktopOpen}
          isMobile={isMobile}
          setSscTimeUp={setSscTimeUp}
          setIsOnSubmit={setIsOnSubmit}
          questionType={question?.question_type}
        />
      </Box>

      {/* Scrollable Content */}
      <Box className={classes.questionNoWrapper}>
        <Typography
          variant="body2"
          fontWeight={"bold"}
          className={classes.questionTitle}
        >
          Q. {currentQuestion + 1}:
        </Typography>
        <IconButton onClick={scrollToBottom} size="small">
          <img src={down} alt="Scroll down" style={{ width: 24, height: 24 }} />
        </IconButton>
      </Box>

      <Box
        className={`${classes.contentContainerMain}
         ${
           !desktopOpen || isMobile
             ? classes.contentContainerMainFull
             : classes.contentContainerMainHaf
         }
      `}
        style={{ display: "flex" }}
      >
        {(question?.paragraph_en || question?.paragraph_hi) && (
          <>
            <Box
              className={classes.contentContainer}
              sx={{
                width: "60%",
                padding: "10px",
                borderRight: "2px solid #000",
                // overflow: "auto",

                // "& img": {
                //   // Targets all <img> inside this Box
                //   width: "100% !important",
                //   height: "auto !important",
                //   maxWidth: "100%",
                // },
              }}
            >
              <Typography
                variant="h6"
                style={{ paddingTop: "5px", paddingBottom: "5px" }}
              >
                Paragraph
              </Typography>
              <span
                onClick={(e) => {
                  // Detect if user clicked an image inside HTML
                  if (e.target.tagName === "IMG") {
                    handleImageClick(e);
                  }
                }}
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html:
                      selectedLanguage === "english"
                        ? question?.paragraph_en || ""
                        : question?.paragraph_hi || "",
                  }}
                />
              </span>
            </Box>
          </>
        )}
        <Box
          sx={{
            width:
              question?.paragraph_en || question?.paragraph_hi ? "40%" : "100%",
          }}
          ref={paperRef}
          className={classes.contentContainer}
        >
          {question ? (
            <QuestionRenderer
              question={question}
              selectedLanguage={selectedLanguage}
              questionType={question?.question_type}
              selectedOptionId={selectedOptionId}
              selectedOptionIds={selectedOptionIds}
              inputAnswer={inputAnswer}
              handleOptionChange={handleOptionChange}
              handleMultipleChoiceChange={handleMultipleChoiceChange}
              handleInputChange={handleInputChange}
              classes={classes}
            />
          ) : (
            <>
              <Loader />
              {/* <Typography>Loading question or no question available</Typography> */}
            </>
          )}
        </Box>
      </Box>
      <div className={classes.upWrapper}>
        <IconButton onClick={scrollToTop} size="small">
          <img src={up} alt="Scroll up" style={{ width: 24, height: 24 }} />
        </IconButton>
      </div>
      <Box className={classes.buttonContainer}>
        <ButtonSection
          handleSaveNextDefault={handleSaveNextDefault}
          handleMarkReviewDefault={handleMarkReviewDefault}
          handleSubmit={handleOpenSubmitModal}
          handleClearResponse={handleClearResponse}
          currentQuestion={currentQuestion}
          question={question}
          questionData={filteredQuestions}
          handleBack={handleBack}
          handleNext={handleNext}
          setActiveSubjectId={setActiveSubjectId}
          setActiveSectionId={setActiveSectionId}
          activeSubjectId={activeSubjectId}
          activeSectionId={activeSectionId}
          subjectNames={subjectNames}
          sectionNames={sectionNames}
        />
      </Box>

      <SubmissionPopupReLogin
        open={showSubmissionPopupReLogin}
        countdown={countdownByHead}
      />

      <SubmissionPopup
        open={showSubmissionPopup}
        countdown={countdownByHead}
        stats={stats}
      />

      <ServerErrorDialog
        open={showServerErrorDialog}
        onClose={() => setShowServerErrorDialog(false)}
      />

      <FinalSubmissionDialog
        open={showSubmitModal || isTimeUp}
        onClose={handleCloseSubmitModal}
        onSubmit={handleFinalSubmit}
        isTimeUp={isTimeUp}
        stats={stats}
        submitError={submitError}
        countdownTime={countdownTime}
        finalSubmitChecked={finalSubmitChecked}
        onFinalSubmitCheck={handleFinalSubmitCheck}
      />

      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
          setTimeout(() => {
            setOpen(false);
          }, 300);
        }}
        maxWidth={false}
        PaperProps={{
          style: {
            width: "60vw",
            height: "65vh",
            // maxHeight: "90vh"
          },
        }}
      >
        <IconButton
          onClick={() => {
            setOpen(false);
            setTimeout(() => {
              setOpen(false);
            }, 300);
          }}
          sx={{
            position: "absolute",
            top: 6,
            right: 6,
            color: "white",
            backgroundColor: "rgba(0,0,0,0.5)",
            "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
            padding: "4px", // smaller button size
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <DialogContent
          sx={{
            p: 0,
            // background: "#000",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <img
            src={selectedImg}
            alt="Question"
            style={{
              width: "100%",
              height: "auto",
              maxHeight: "100%",
              objectFit: "contain",
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default QuestionSection;
